/**
 * Whether a team held on the session should be restored into the step.
 *
 * Identical reasoning to `shouldRestoreSessionDivision`, and deliberately a
 * separate call rather than a shared one: the team and the division are cleared
 * independently (switching divisions drops the team but keeps a division), so
 * they need their own "cleared by the reader" flags.
 */
export const shouldRestoreSessionTeam = ({
  sessionTeamId,
  hasSelection,
  clearedByReader,
  hasDeepLink = false,
}: {
  sessionTeamId?: string | null
  /** A team is already chosen, so there is nothing to restore. */
  hasSelection: boolean
  /** The reader deliberately cleared it since the session was last written. */
  clearedByReader: boolean
  /** A `?team_id=` link outranks the session — see the division equivalent. */
  hasDeepLink?: boolean
}): boolean => {
  if (!sessionTeamId || hasSelection || clearedByReader || hasDeepLink) {
    return false
  }
  return true
}

/**
 * Whether a division held on the session should be restored into the step.
 *
 * The session is only rewritten when the reader presses Next, so between a
 * deselect and the next submit it still names the division they just cleared.
 * A restore that looks only at "nothing is selected" therefore puts that
 * division straight back on the next render, and the deselect appears not to
 * work at all.
 *
 * The reader's own clear outranks the stale session value. It stands until they
 * answer the question again — by choosing a division, or a team, which resolves
 * one.
 */
export const shouldRestoreSessionDivision = ({
  sessionDivisionId,
  hasSelection,
  clearedByReader,
  hasDeepLink = false,
}: {
  sessionDivisionId?: string | null
  /** A division is already chosen, so there is nothing to restore. */
  hasSelection: boolean
  /** The reader deliberately cleared it since the session was last written. */
  clearedByReader: boolean
  /**
   * The URL names a division or a team, either of which resolves one.
   *
   * A link outranks the session for the same reason a deliberate clear does: it
   * is a fresh, explicit choice. Both this and the pre-select fire on an empty
   * selection, so without this the two would race and the session could win —
   * silently showing a different division than the link the reader clicked.
   */
  hasDeepLink?: boolean
}): boolean => {
  if (!sessionDivisionId || hasSelection || clearedByReader || hasDeepLink) {
    return false
  }
  return true
}
